import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { Theme } from '@emotion/react';
import { clone, ConfigModel, removeUndefinedProps } from '@ibe/services';
import { ApiBaseData, ApiGeoUnit, ApiRentalCarOffice, ApiRoomContainer } from '@ibe/api';
import PackageParams from '../PackageDetails/PackageParams';
import PackageSearchParams from '../PackageSearch/PackageSearchParams';
import DestinationItem from '../../../Models/DestinationItem';

const ADD_DAYS_TO_START = 7;
const ADD_DAYS_TO_END = 14;

class BestPriceAvailabilitySearchStore {
  error: string | null = null;

  isReady = false;

  _directFlightsOnlyConfigured = false;

  partialSearchParams: Partial<PackageSearchParams> = { directFlightsOnly: true };

  theme?: Theme;

  config: ConfigModel;

  defaultSearchParams: PackageSearchParams;

  constructor(
    config: ConfigModel,
    theme?: Theme,
    startWithEmptyDates?: boolean,
    initialSearchParams?: Partial<PackageSearchParams>
  ) {
    makeObservable(this, {
      isReady: observable,
      _directFlightsOnlyConfigured: observable,
      error: observable,
      partialSearchParams: observable,

      searchParams: computed,
      startDate: computed,
      fullStartDate: computed,
      fullEndDate: computed,
      origin: computed,
      origins: computed,
      destination: computed,
      packageServiceGroup: computed,
      destinationItem: computed,

      setDestinationItem: action,
      setDestination: action,
      setOrigin: action,
      setOrigins: action,
      setDate: action,
      setOccupancy: action,
      setParams: action,
      setStartDate: action,
      setEndDate: action
    });
    this.theme = theme;
    this.config = config;
    this.defaultSearchParams = {
      origin: undefined,
      origins: [],
      destination: undefined,
      startDate: dayjs()
        .add(
          this.theme?.dateRangePickerOptions?.defaultDaysAddedToStart || ADD_DAYS_TO_START,
          'day'
        )
        .format(this.config?.formatDate),
      endDate: dayjs()
        .add(this.theme?.dateRangePickerOptions?.defaultDaysAddedToEnd || ADD_DAYS_TO_END, 'day')
        .format(this.config?.formatDate),
      occupancy: [
        {
          adults: 2,
          children: 0,
          infants: 0,
          childrenAges: [],
          infantsAges: []
        }
      ],
      packageServiceGroup: ''
    };
    if (startWithEmptyDates) {
      this.defaultSearchParams = {
        ...this.defaultSearchParams,
        startDate: undefined,
        endDate: undefined
      };
    }

    if (!!initialSearchParams) {
      this.setParams(initialSearchParams);
    }
  }

  get duration(): number {
    return this.searchParams.duration || 0;
  }

  setParams(p: Partial<PackageSearchParams>): void {
    if (!isEqual(toJS(this.partialSearchParams), p)) {
      this.partialSearchParams = removeUndefinedProps({
        ...(clone(toJS(this.partialSearchParams || {})) as Partial<PackageSearchParams>),
        ...p
      });
    }
  }

  private fillWithDefaults(values: Partial<PackageSearchParams>): PackageSearchParams {
    return {
      ...(clone(toJS(this.defaultSearchParams) || {}) as Partial<PackageSearchParams>),
      ...removeUndefinedProps<PackageSearchParams>(values || {})
    };
  }

  resetDetail(): void {
    (this.searchParams as PackageParams).packageDetailId = undefined;
    (this.searchParams as PackageParams).itemId = undefined;
  }

  get searchParams(): PackageSearchParams {
    return this.fillWithDefaults(this.partialSearchParams);
  }

  get origin(): ApiGeoUnit | undefined {
    return this.searchParams.origin;
  }

  get origins(): ApiGeoUnit[] | undefined {
    return this.searchParams.origins;
  }

  get destination(): ApiGeoUnit | undefined {
    return this.searchParams.destination;
  }

  get destinationItem(): DestinationItem | undefined {
    return this.searchParams.destinationItem;
  }

  get startDate(): Date {
    const date = dayjs(this.searchParams.startDate, this.config.formatDate);
    return date.startOf('month').toDate();
  }

  get fullStartDate(): string {
    return this.searchParams.startDate || '';
  }

  get fullEndDate(): string {
    return this.searchParams.endDate || '';
  }

  get packageServiceGroup(): string {
    return this.searchParams.packageServiceGroup || '';
  }

  setOrigin(origin: ApiGeoUnit | undefined): void {
    this.setParams({ origin });
  }

  setOrigins(origins: ApiGeoUnit[]): void {
    this.setParams({ origins });
  }

  setDestination(destination: ApiGeoUnit | undefined): void {
    this.setParams({ destination });
  }

  setDestinationItem(destinationItem: DestinationItem | undefined): void {
    this.setParams({ destinationItem });
  }

  setDate(date: Date | null): void {
    const params = clone(toJS(this.partialSearchParams)) as Partial<PackageSearchParams>;

    params.startDate = undefined;
    params.endDate = undefined;
    if (date) {
      const d = dayjs(date);
      params.startDate = d.startOf('month').format(this.config.formatDate);
      const addMonthToEnd = 1;
      params.endDate = d
        .startOf('month')
        .add(addMonthToEnd, 'month')
        .format(this.config.formatDate);
    }

    this.partialSearchParams = params;
  }

  setStartDate(date: string | null): void {
    this.setParams({ startDate: !!date ? dayjs(date).format(this.config.formatDate) : undefined });
  }

  setEndDate(date: string | null): void {
    this.setParams({ endDate: !!date ? dayjs(date).format(this.config.formatDate) : undefined });
  }

  setOccupancy(o: ApiRoomContainer[]): void {
    this.setParams({ occupancy: o || undefined });
  }

  get occupancy(): ApiRoomContainer[] {
    return this.searchParams.occupancy || [];
  }

  setPostalCode(postalCode: string | undefined): void {
    this.setParams({ postalCode: postalCode || undefined });
  }

  get postalCode(): string | undefined {
    return this.searchParams.postalCode;
  }

  setSelectedPostalData(postalData: ApiBaseData | undefined): void {
    this.setParams({ selectedPostalData: postalData || undefined });
  }

  get selectedPostalData(): ApiBaseData | undefined {
    return this.searchParams.selectedPostalData;
  }

  setBusStations(busStations: ApiRentalCarOffice[] | undefined): void {
    this.setParams({ busStations: busStations || undefined });
  }

  get busStations(): ApiRentalCarOffice[] | undefined {
    return this.searchParams.busStations;
  }

  setBusStationsAvailable(available: boolean): void {
    this.setParams({ busStationsAvailable: available });
  }

  get isBusStationsAvailable(): boolean {
    return this.searchParams.busStationsAvailable ?? false;
  }

  setDuration(duration: number | undefined): void {
    this.setParams({ duration });
  }

  setDirectFlightsOnly(directFlightsOnly: boolean): void {
    this.setParams({ directFlightsOnly });
  }

  get directFlightsOnly(): boolean {
    return this.searchParams.directFlightsOnly || false;
  }

  setDirectFlightsOnlyConfigured(directFlightsConfigured: boolean) {
    this._directFlightsOnlyConfigured = directFlightsConfigured;
  }

  get directFlightsOnlyConfigured(): boolean {
    return this._directFlightsOnlyConfigured;
  }
}

export default BestPriceAvailabilitySearchStore;
